/**
 * Rules for keying a deal in by hand — the Backshop dual-entry path (LPR-16).
 *
 * The catalogue marks 18 fields `required`: that is Backshop's list of what a
 * complete record holds, and the dossier reports any that are still blank. It
 * is not what a deal needs in order to exist. Blocking the create on all of them
 * forced people to invent values — a brokerage for a direct-sponsor deal, a
 * note name before one is assigned — which is worse than a visible gap.
 *
 * Client item #1 made four of them a requirement at intake, and those DO block
 * a person's create: the loan purpose, the city and state, the initial loan
 * amount, and the fully funded (max) loan amount. `intakeRequiredErrors` is the
 * mirror of `app/services/intake_required.py`, so the form says inline what the
 * server would otherwise refuse.
 */
import { DESK_TIME_ZONE } from '@/utils/dates'
import { parseMoneyText, type DealFieldDef } from '@/services/dealFile'
import { assumedValue } from './intakeDefaults'

/** What the server needs to create a deal. Location may come from City/State. */
const CREATE_REQUIRED = new Set([
  'property_name',
  'asset_type',
  'sponsor',
  'loan_amount',
  'location',
  // Client item #1 — required at intake.
  'purpose',
  'city',
  'state',
  'fully_funded_amount',
])

/** The four the client named (city and state are two fields): field -> label. */
export const INTAKE_REQUIRED: ReadonlyArray<readonly [string, string]> = [
  ['purpose', 'Loan Purpose'],
  ['city', 'City'],
  ['state', 'State / Province'],
  ['loan_amount', 'Initial Loan Amount'],
  ['fully_funded_amount', 'Fully Funded / Max Loan Amount'],
]

/**
 * The day it is at the desk (New York), as the "YYYY-MM-DD" a date input holds.
 *
 * A deal's Date Received is the desk's calendar day, not the browser's and not
 * UTC's: a package keyed in at 9pm Eastern is still today's.
 */
export function deskToday(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: DESK_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const pick = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${pick('year')}-${pick('month')}-${pick('day')}`
}

/** The values a fresh form starts with — and starts with again for the next deal.
 *  The stage, and the Responsible Party's own default category: a pre-filled
 *  priority ("Medium") or country ("US") was saved as if the person had typed it
 *  (package E, LPR-07), so nothing else is. Not here either: the position, the
 *  currency and the unit of measure — the form shows what the platform will assume
 *  ("Senior (default)") and the server records it as a default, not as typing.
 *  The received date is separate and is set per form (`freshEntryValues`). */
export const MANUAL_ENTRY_DEFAULTS: Readonly<Record<string, string>> = Object.freeze({
  stage: 'Triage',
  assignee: 'Unassigned',
})

/** A fresh form: the defaults, and the day the package is being received. The
 *  received date is the person's to change; the creation timestamp is stamped by
 *  the system and never typed. */
export function freshEntryValues(now: Date = new Date()): Record<string, string> {
  return { ...MANUAL_ENTRY_DEFAULTS, originated: deskToday(now) }
}

export function blocksCreate(def: DealFieldDef): boolean {
  return CREATE_REQUIRED.has(def.name)
}

/** Backshop-required fields left blank: reported after create, never blocking. */
export function missingBackshopRequired(fields: DealFieldDef[], values: Record<string, string>): string[] {
  return fields
    .filter((f) => f.required && !blocksCreate(f) && !(values[f.name] || '').trim())
    .filter((f) => !assumedValue(f.name, values.asset_type))
    .map((f) => f.label)
}

const money = (raw: string | undefined): number | null => {
  const text = (raw ?? '').trim()
  return text ? parseMoneyText(text) : null
}

/** $ and thousands separators while an amount is being typed. */
export function formatCurrencyTyping(raw: string): string {
  const amount = money(raw)
  if (amount === null) return raw
  const negative = amount < 0
  const abs = Math.abs(amount)
  const compact = raw.replace(/\s/g, '')
  const scaled = /(?:k|mm|mn|m|bn|b)$/i.test(compact.replace(/\$/g, ''))
  const [whole, frac] = abs.toFixed(abs % 1 === 0 ? 0 : 2).split('.')
  const grouped = Number(whole).toLocaleString('en-US')
  if (!scaled) {
    const typedFrac = compact.replace(/[$,-]/g, '').match(/\.(\d*)/)
    if (typedFrac) {
      return `${negative ? '-' : ''}$${grouped}.${typedFrac[1].slice(0, 2)}`
    }
  }
  return `${negative ? '-' : ''}$${grouped}${frac ? `.${frac}` : ''}`
}

/**
 * Each of the four required fields the entry does not satisfy, with the message
 * that goes under its box. Empty when the deal may be created.
 *
 * With nothing held back the fully funded amount IS the initial amount, so it is
 * satisfied by that default; it only blocks where it cannot default (no initial
 * amount), contradicts the amount it must contain, or future funding is stated
 * without an initial advance for the commitment arithmetic to run on.
 */
export function intakeRequiredErrors(values: Record<string, string>): Record<string, string> {
  const errors: Record<string, string> = {}
  if (!(values.purpose || '').trim()) errors.purpose = 'Required'
  if (!(values.city || '').trim()) errors.city = 'Required'
  if (!(values.state || '').trim()) errors.state = 'Required'
  if ((values.state || '').trim().toUpperCase() === 'XX' && !(values.country || '').trim()) {
    errors.country = 'Required for an international location'
  }

  const initial = money(values.loan_amount)
  if (initial === null || !(initial > 0)) {
    errors.loan_amount = (values.loan_amount || '').trim() && initial === null ? 'Enter an amount' : 'Required'
  }

  const totalText = (values.fully_funded_amount || '').trim()
  const total = money(totalText)
  if (totalText && total === null) {
    errors.fully_funded_amount = 'Enter an amount'
  } else if (total !== null && !(total > 0)) {
    errors.fully_funded_amount = 'Required'
  } else if (total === null) {
    const future = money(values.future_funding)
    const advance = money(values.initial_funding)
    if (initial === null || !(initial > 0)) errors.fully_funded_amount = 'Required'
    else if (future !== null && future > 0 && advance === null) {
      errors.fully_funded_amount = 'Required — future funding is stated, so the total is not the initial amount'
    }
  } else if (initial !== null && initial > 0 && total < initial && Math.abs(total - initial) > 0.005 * Math.max(total, initial)) {
    errors.fully_funded_amount = 'Cannot be below the Initial Loan Amount'
  }
  return errors
}

function plain(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

/**
 * What the Fully Funded Amount box offers while nobody has typed in it: the
 * initial amount plus any future funding. "" when there is no initial amount to
 * offer it from. A starting point the person can overwrite — once they type
 * there, the form stops offering.
 */
export function offeredTotal(values: Record<string, string>): string {
  const initial = money(values.loan_amount)
  if (initial === null || !(initial > 0)) return ''
  const future = money(values.future_funding)
  return plain(initial + (future !== null && future > 0 ? future : 0))
}

/**
 * The server's refusal of a create (`required_fields_missing`) as the messages
 * that go under each box, or null when the error is something else.
 */
export function requiredFieldErrorsFromRefusal(detail: unknown): Record<string, string> | null {
  if (!detail || typeof detail !== 'object') return null
  const body = detail as { code?: unknown; fields?: unknown }
  if (body.code !== 'required_fields_missing' || !Array.isArray(body.fields)) return null
  const out: Record<string, string> = {}
  for (const entry of body.fields) {
    if (!entry || typeof entry !== 'object') continue
    const { field, message } = entry as { field?: unknown; message?: unknown }
    if (typeof field === 'string') out[field] = typeof message === 'string' && message ? message : 'Required'
  }
  return Object.keys(out).length ? out : null
}

/**
 * Stages a deal may be created at. Never Funded (funding is its own gated
 * step and the server refuses it), and only Triage for someone who is not an
 * admin: moving a deal past Triage is admin-only until roles are set up.
 */
export function stageChoices(options: readonly string[], isAdmin: boolean): string[] {
  const allowed = options.filter((o) => o !== 'Funded')
  return isAdmin ? allowed : allowed.filter((o) => o === 'Triage')
}

export function canPlacePastTriage(
  user: { role?: string | null; is_super_admin?: boolean | null } | null | undefined,
): boolean {
  if (!user) return false
  if (user.is_super_admin) return true
  const role = String(user.role ?? '').toLowerCase()
  return role === 'admin' || role === 'super_admin'
}
